// Les CARTES d'études recommandées — partagées par le tableau de bord (bloc
// « Études · orientation ») et la page Études (bloc « issues de la mesure 0 »).
//
// Chaque carte est repliée (priorité, type, titre) ; un clic l'ouvre. Elle se crée en étude, se reformule (crayon) ou s'écarte
// (poubelle).
// Écarter n'efface rien : la recommandation passe dans « Écartées » et se
// restaure. Les RAISONS ne se modifient pas — on peut réécrire une
// recommandation, pas réécrire les faits qui l'ont déclenchée.
import { useId, useState, type FormEvent } from 'react';
import { useAdjustRecommendation, useCreateStudy } from '../api/queries';
import { ApiRequestError } from '../api/client';
import type { RecPriority, StudyRecommendation } from '../api/types';
import { Button, Chip, ErrorBanner, Field, IconButton, Select } from '../components/ui';
import { Link, useRouter } from '../router';
import { TextArea } from '../write/Forms';
import { Drawer } from '../write/Drawer';
import { studyKind } from '../studies/kinds';
import { KIND_STYLE } from '../taxonomy/colors';
import './plan.css';

const PRIORITY_LABELS: Record<RecPriority, string> = { haute: 'Priorité haute', moyenne: 'Priorité moyenne', basse: 'Priorité basse' };
const STUDY_TYPES = ['Entretiens', 'Observation terrain', 'Test utilisateur', 'Questionnaire', 'Analyse de données', 'Revue documentaire', 'Audit UX'];

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

/** Liste complète : ouvertes (limitées si `limit`), déjà planifiées, écartées. */
export function RecommendationList({
  projectId, recommendations, canWrite, limit,
}: {
  projectId: string; recommendations: StudyRecommendation[]; canWrite: boolean; limit?: number;
}) {
  const [showAll, setShowAll] = useState(false);
  const open = recommendations.filter((r) => r.status === 'open');
  const planned = recommendations.filter((r) => r.status === 'planned');
  const dismissed = recommendations.filter((r) => r.status === 'dismissed');
  const shown = limit && !showAll ? open.slice(0, limit) : open;

  return (
    <>
      {open.length === 0 ? (
        <p className="zy-texte-muet">
          Aucune étude en attente : chaque recommandation a son étude ou a été écartée. Le plan se remet à
          jour dès qu’un résultat, une note ou la mesure 0 change.
        </p>
      ) : (
        <ol className="plan-recs">
          {shown.map((r) => (
            <RecommendationCard key={r.key} rec={r} projectId={projectId} canWrite={canWrite} />
          ))}
        </ol>
      )}
      {limit && open.length > limit ? (
        <Button variant="ghost" onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
          {showAll ? 'Afficher moins' : `Voir les ${open.length - limit} autres recommandations`}
        </Button>
      ) : null}

      {planned.length > 0 ? (
        <details className="plan-planned">
          <summary>Déjà planifiées · {planned.length}</summary>
          <ul>
            {planned.map((r) => (
              <li key={r.key}>
                <span>{r.title}</span>
                {r.studyId ? (
                  <Link to={`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(r.studyId)}`}>
                    Voir l’étude
                  </Link>
                ) : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {dismissed.length > 0 ? (
        <details className="plan-planned">
          <summary>Écartées · {dismissed.length}</summary>
          <ul>
            {dismissed.map((r) => (
              <DismissedRow key={r.key} rec={r} projectId={projectId} canWrite={canWrite} />
            ))}
          </ul>
        </details>
      ) : null}
    </>
  );
}

function DismissedRow({ rec, projectId, canWrite }: { rec: StudyRecommendation; projectId: string; canWrite: boolean }) {
  const adjust = useAdjustRecommendation(projectId);
  return (
    <li>
      <span>{rec.title}</span>
      {canWrite ? (
        <button type="button" className="plan-link-btn" disabled={adjust.isPending} onClick={() => adjust.mutate({ key: rec.key, action: 'restore' })}>
          Restaurer
        </button>
      ) : null}
    </li>
  );
}

/** Un clic sur la carte l'ouvre — sauf s'il vise un bouton, un lien ou un champ. */
function toggleFromCard(e: React.MouseEvent, toggle: () => void) {
  if ((e.target as HTMLElement).closest('button, a, input, textarea, select, label, .plan-rec-detail, .plan-rec-actions')) return;
  toggle();
}

export function RecommendationCard({ rec, projectId, canWrite }: { rec: StudyRecommendation; projectId: string; canWrite: boolean }) {
  const create = useCreateStudy(projectId);
  const adjust = useAdjustRecommendation(projectId);
  // La carte est repliée par défaut et s'OUVRE AU CLIC (objectif, cible,
  // raisons, création de l'étude). « Creuser » n'est plus un bouton d'ouverture :
  // le mot est réservé à l'approfondissement d'un insight par l'IA.
  const [open, setOpen] = useState(false);
  const detailId = useId();
  const [editing, setEditing] = useState(false);
  const { navigate } = useRouter();

  // Une fois créée, on ENTRE dans l'étude. Un lien « l'ouvrir » affiché ici ne
  // servirait à rien : la recommandation passe aussitôt dans « Déjà
  // planifiées » et cette carte disparaît avec lui.
  async function createStudy() {
    try {
      const { study } = await create.mutateAsync({
        type: rec.type,
        objective: rec.objective,
        target: rec.target,
        context: `Recommandée par le plan d’études (${rec.rule}). ${rec.reasons.join(' ')}`,
        status: 'Planifiée',
        planKey: rec.key,
      });
      navigate(`/projet/${encodeURIComponent(projectId)}/etudes/${encodeURIComponent(study.id)}`);
    } catch {
      /* rendu par le bandeau */
    }
  }

  if (editing) {
    return (
      <li className={`plan-rec plan-rec-${rec.priority}`}>
        <EditForm rec={rec} projectId={projectId} onDone={() => setEditing(false)} />
      </li>
    );
  }

  return (
    <li className={`plan-rec plan-rec-click plan-rec-${rec.priority}`} onClick={(e) => toggleFromCard(e, () => setOpen((v) => !v))}>
      <div className="plan-rec-top">
        <span className={`plan-prio plan-prio-${rec.priority}`}>{PRIORITY_LABELS[rec.priority]}</span>
        <Chip hue={studyKind(rec.type) ? KIND_STYLE[studyKind(rec.type)!].hue : undefined}>{rec.type}</Chip>
        {rec.pillarName ? <span className="plan-pillar">{rec.pillarName}</span> : null}
        {rec.edited ? <span className="plan-edited" title="Reformulée par l’équipe">modifiée</span> : null}
        <span className="plan-rec-tools">
          {canWrite ? (
            <>
              <IconButton icon="edit" label={`Modifier « ${rec.title} »`} onClick={() => setEditing(true)} />
              <IconButton icon="trash" danger label={`Écarter « ${rec.title} »`} onClick={() => adjust.mutate({ key: rec.key, action: 'dismiss' })} />
            </>
          ) : null}
        </span>
      </div>
      {/* Le titre est le VRAI déclencheur (clavier, lecteur d’écran) ; le clic
          ailleurs sur la carte n’est qu’un confort de souris. */}
      <button type="button" className="plan-rec-title plan-rec-open" aria-expanded={open} aria-controls={detailId} onClick={() => setOpen((v) => !v)}>
        {rec.title}
      </button>

      {adjust.error ? <ErrorBanner>{errorMessage(adjust.error)}</ErrorBanner> : null}
      {open ? (
        <div className="plan-rec-detail" id={detailId}>
          <p className="plan-rec-obj">{rec.objective}</p>
          <p className="plan-rec-target">
            <span>Cible</span> {rec.target}
          </p>
          <p className="plan-rec-target">
            <span>Pourquoi cette étude</span>
          </p>
          <ul className="plan-reasons">
            {rec.reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ul>
          {create.error ? <ErrorBanner>{errorMessage(create.error)}</ErrorBanner> : null}
        </div>
      ) : null}
      {open && canWrite ? (
        <div className="plan-rec-actions">
          <Button variant={rec.priority === 'haute' ? 'primary' : 'ghost'} loading={create.isPending} onClick={() => void createStudy()}>
            Créer cette étude
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function EditForm({ rec, projectId, onDone }: { rec: StudyRecommendation; projectId: string; onDone: () => void }) {
  const adjust = useAdjustRecommendation(projectId);
  const [title, setTitle] = useState(rec.title);
  const [objective, setObjective] = useState(rec.objective);
  const [target, setTarget] = useState(rec.target);
  const [type, setType] = useState(rec.type);
  const [priority, setPriority] = useState<RecPriority>(rec.priority);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await adjust.mutateAsync({ key: rec.key, action: 'edit', patch: { title, objective, target, type, priority } });
      onDone();
    } catch {
      /* rendu par le bandeau */
    }
  }

  return (
    <form className="write-form plan-edit" onSubmit={submit} noValidate>
      <p className="opt-subtitle">Modifier la recommandation</p>
      <Field label="Titre" value={title} onChange={(e) => setTitle(e.target.value)} />
      <TextArea label="Objectif" value={objective} onChange={setObjective} rows={3} />
      <Field label="Cible" value={target} onChange={(e) => setTarget(e.target.value)} />
      <div className="plan-edit-row">
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={`type-${rec.key}`}>Type d’étude</label>
          <input id={`type-${rec.key}`} className="zy-input" list={`types-${rec.key}`} value={type} onChange={(e) => setType(e.target.value)} />
          <datalist id={`types-${rec.key}`}>
            {STUDY_TYPES.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div className="zy-field">
          <label className="zy-field__label" htmlFor={`prio-${rec.key}`}>Priorité</label>
          <Select id={`prio-${rec.key}`} value={priority} onChange={(e) => setPriority(e.target.value as RecPriority)}>
            <option value="haute">Haute</option>
            <option value="moyenne">Moyenne</option>
            <option value="basse">Basse</option>
          </Select>
        </div>
      </div>
      <p className="zy-texte-muet">Les raisons de la recommandation ne changent pas : elles viennent des données du projet.</p>
      {adjust.error ? <ErrorBanner>{errorMessage(adjust.error)}</ErrorBanner> : null}
      <div className="plan-rec-actions plan-edit-actions">
        <Button type="button" variant="ghost" onClick={onDone}>Annuler</Button>
        <Button type="submit" variant="accent" loading={adjust.isPending} disabled={!title.trim() || !objective.trim()}>Enregistrer</Button>
      </div>
    </form>
  );
}

/** Bouton « Recommandations » de la page Études : les études à mener, d'après
 *  le contexte du projet (mesure 0) et les preuves déjà réunies, dans un
 *  panneau — la liste des études reste la liste des études. */
export function StudiesRecommendations({ projectId, canWrite, plan }: {
  projectId: string; canWrite: boolean; plan: { recommendations: StudyRecommendation[] } | undefined;
}) {
  const [open, setOpen] = useState(false);
  if (!plan) return null;
  const count = plan.recommendations.filter((r) => r.status === 'open').length;
  return (
    <>
      <Button variant="ghost" onClick={() => setOpen(true)}>
        Recommandations <span className="plan-count">{count}</span>
      </Button>
      <Drawer
        open={open}
        title="Études recommandées"
        description="Proposées à partir du contexte du projet et des preuves déjà réunies : entretiens avec les utilisateurs, chiffres d’usage, questionnaires… Créez-les en étude, reformulez-les, ou écartez celles qui ne s’appliquent pas."
        wide
        onClose={() => setOpen(false)}
      >
        {plan.recommendations.length === 0 ? (
          <p className="zy-texte-muet">
            Aucune recommandation pour l’instant. Complétez l’état des lieux du projet depuis le tableau de
            bord : les recommandations en découlent.
          </p>
        ) : (
          <RecommendationList projectId={projectId} recommendations={plan.recommendations} canWrite={canWrite} />
        )}
      </Drawer>
    </>
  );
}
